import React from 'react';
import type { ComponentType, ReactNode } from 'react';
import { cn } from '../../lib/utils';

type EmptyStateIcon = ReactNode | ComponentType<{ className?: string }>;

export interface RegistryEmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: EmptyStateIcon;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link';
}

export interface RegistryEmptyStateProps {
  icon: EmptyStateIcon;
  title: string;
  description: string;
  helperText?: string;
  primaryAction?: RegistryEmptyStateAction;
  secondaryAction?: RegistryEmptyStateAction;
  className?: string;
}

/**
 * A visually rich empty state component for use within ManagementRegistry 
 * or other large-scale worksurfaces.
 */
export function RegistryEmptyState({
  icon,
  title,
  description,
  helperText,
  primaryAction,
  secondaryAction,
  className
}: RegistryEmptyStateProps) {
  const isComponentIcon = (
    iconValue: EmptyStateIcon
  ): iconValue is ComponentType<{ className?: string }> => {
    if (typeof iconValue === 'function') {
      return true;
    }

    return Boolean(iconValue && typeof iconValue === 'object' && '$$typeof' in iconValue);
  };

  const renderIcon = (iconValue: EmptyStateIcon, className: string) => {
    if (React.isValidElement(iconValue)) {
      return React.cloneElement(iconValue as React.ReactElement<any>, {
        className: cn(className, (iconValue.props as { className?: string }).className),
      });
    }

    if (isComponentIcon(iconValue)) {
      const IconComponent = iconValue as unknown as ComponentType<{ className?: string }>;
      return <IconComponent className={className} />;
    }

    return iconValue;
  };

  return (
    <div className={cn(
      "flex flex-col items-center justify-center max-w-md mx-auto text-center py-12 px-6 bg-muted/30 rounded-2xl border border-dashed border-border/60",
      className
    )}>
      <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6 shadow-sm ring-1 ring-primary/20">
        {renderIcon(icon, "w-8 h-8")}
      </div>
      
      <h3 className="text-xl font-bold mb-3 text-foreground">
        {title}
      </h3>
      
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        {description}
      </p>
      
      {helperText && (
        <p className="text-xs text-muted-foreground/60 mb-8 italic">
          {helperText}
        </p>
      )}
      
      {(primaryAction || secondaryAction) && (
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {primaryAction && (
            <button
              onClick={primaryAction.onClick}
              className={cn(
                "w-full sm:w-auto rounded-lg px-6 py-2.5 text-sm font-semibold transition-all shadow-sm ring-1 ring-primary/20",
                "bg-primary text-primary-foreground hover:opacity-90 active:scale-[0.98]"
              )}
            >
              <div className="flex items-center justify-center">
                {primaryAction.icon && (
                  <span className="mr-2">{renderIcon(primaryAction.icon, "w-4 h-4")}</span>
                )}
                {primaryAction.label}
              </div>
            </button>
          )}
          
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className={cn(
                "w-full sm:w-auto rounded-lg px-6 py-2.5 text-sm font-semibold transition-all shadow-sm ring-1",
                "bg-muted text-muted-foreground hover:bg-muted/80 ring-border/50 active:scale-[0.98]"
              )}
            >
              <div className="flex items-center justify-center">
                {secondaryAction.icon && (
                  <span className="mr-2">{renderIcon(secondaryAction.icon, "w-4 h-4")}</span>
                )}
                {secondaryAction.label}
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
